import axios from 'axios';
import { Formik } from 'formik';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SEND_RECEIPT } from '../../constants/paths/api';
import { CLIENT_ID } from '../../helpers/mobizHelpers/configurations';
import { ReactComponent as IconInfo } from '../../icons/info.svg';
import IPageViewModel from '../../interfaces/umbracoNodes/pageViewModel';
import { Column, Columns } from '../base/grid';
import { A, P } from '../base/typography';
import CtaIconBox from '../ctaBoxes/ctaIconBox';
import SuccessOrFailure from '../successOrFailure';
import { sendReceiptInitialValues, sendReceiptSchema } from './sendReceipt.schema';
import * as StyledSendReceipt from './sendReceipt.styles';
import SendReceiptForm from './sendReceiptForm';

interface Props {
	page: IPageViewModel;
}

const SendReceipt: FC<Props> = ({ page }) => {
	const { t } = useTranslation('sendReceipt');
	const messages = [t('processTime'), t('ifWeNeedMore')];
	const [hasSubmitted, setHasSubmitted] = useState(false);
	const [hasSubmittedSuccessfully, setHasSubmittedSucessfully] = useState(false);
	const [error, setError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	// This is now always true since we alawys want phone to be optional
	sendReceiptInitialValues.isCompany = true;

	const onStepInfoSubmit = (values, actions) => {
		const { files, foreignSsn, ...jsonObject } = values;
		const formData = new FormData();

		// jsonObject.hasReceipt = jsonObject.hasReceipt === 'Yes';
		switch (jsonObject.instructionsToStaff) {
			case 'openUp':
				jsonObject.instructionsToStaff = '1';
				break;
			case 'gift':
				jsonObject.instructionsToStaff = '2';
				break;
			case 'other':
				jsonObject.instructionsToStaff = '3';
				break;
			default:
				jsonObject.instructionsToStaff = '0';
				break;
		}

		files.forEach(file => {
			formData.append('Documents', file);
		});

		formData.append('AdditionalInformation', jsonObject.additionalInformation);
		formData.append('InstructionsForHandling', jsonObject.instructionsToStaff);
		formData.append('DeliveryMethod', jsonObject.deliveryService);
		formData.append('IdentificationNumber', jsonObject.identificationNumber);
		formData.append('Name', jsonObject.name);
		formData.append('Phone', jsonObject.phone);
		formData.append('Email', jsonObject.email);

		let requestUrl = SEND_RECEIPT;
		jsonObject.trackingNumbers.forEach(
			(trackingNumber, i) =>
				(requestUrl += `${i === 0 ? '?shipmentIdsList=' : '&shipmentIdsList='}${trackingNumber}`)
		);

		axios
			.post(requestUrl, formData, {
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
					'x-clientId': CLIENT_ID,
				},
			})
			.then(({ data }) => {
				actions.setSubmitting(false);
				setHasSubmitted(true);
				setHasSubmittedSucessfully(!data.error);
				setError(data.error);
				setErrorMessage(data.errorMessage);

				if (!data.error) window.scrollTo(0, 0);
			})
			.catch(e => {
				actions.setSubmitting(false);
				setHasSubmitted(true);
				setHasSubmittedSucessfully(false);
				setError(true);
				e && e.response && e.response.data && e.response.data.details
					? setErrorMessage(e.response.data.details)
					: setErrorMessage(e.message);
			});
	};

	return (
		<StyledSendReceipt.Wrapper>
			{!hasSubmittedSuccessfully && (
				<>
					<P>
						{t('info')}
						<A href="/einstaklingar/mottaka/reiknivel-erlendra-sendinga/">{`(${t('seeCalculator')})`}</A>.
					</P>
					<P>{t('info2')}</P>
					<StyledSendReceipt.InfoTextWrapper>
						<IconInfo />
						<P>{t('moreThanOneTrackingnumber')}</P>
					</StyledSendReceipt.InfoTextWrapper>
					<Formik
						initialValues={sendReceiptInitialValues}
						enableReinitialize
						validationSchema={sendReceiptSchema}
						onSubmit={onStepInfoSubmit}>
						{({ values, setFieldTouched, setFieldValue, isValid, errors, isSubmitting }) => (
							<SendReceiptForm
								values={values}
								setFieldTouched={setFieldTouched}
								setFieldValue={setFieldValue}
								isSubmitting={isSubmitting}
								isValid={isValid}
								errors={errors}
							/>
						)}
					</Formik>
				</>
			)}
			<SuccessOrFailure
				hasSubmitted={hasSubmitted}
				success={!error}
				messageArray={messages}
				errorMessage={errorMessage}
			/>
			{hasSubmittedSuccessfully && !error && (
				<>
					<StyledSendReceipt.CtaBoxWrapper>
						<Columns>
							{page.Values.CtaBoxes.map((box, index) => (
								<Column key={index} mobile={1 / 2}>
									<CtaIconBox box={box} />
								</Column>
							))}
						</Columns>
					</StyledSendReceipt.CtaBoxWrapper>
				</>
			)}
		</StyledSendReceipt.Wrapper>
	);
};

export default SendReceipt;
